import { getIsTravelApp, isCorpTenant } from "@capone/common";
import {
  BusinessModalNames,
  CorpBusinessAccount,
  CorpSessionInfo,
  CustomerAccountRole,
  ModalDataMap,
  PolicyTier,
  RewardsAccount,
  SessionInfo,
  Tenant,
} from "redmond";
import { getIsDefaultPolicySelected } from "./getIsDefaultPolicySelected";
import { getUserDeviceData } from "./userDeviceHelper";

type StoreState<T extends Tenant> = {
  sessionInfo?: T extends "capone-corporate" ? CorpSessionInfo : SessionInfo;
  corpBusinessAccounts?: CorpBusinessAccount[];
  rewardsAccounts?: RewardsAccount[];
  selectedAccountReferenceId?: string | null;
  policyTier?: PolicyTier;
  modalMetadata?: { [K in BusinessModalNames]?: ModalDataMap[K] } | null;
  isMultiportal?: boolean;
};

const getCorpUserSessionData = (
  state: StoreState<"capone-corporate">,
  customerAccountRole?: CustomerAccountRole
) => {
  const {
    sessionInfo,
    corpBusinessAccounts = [],
    modalMetadata,
    policyTier,
    isMultiportal,
  } = state;

  const approvalProcess =
    policyTier?.settings?.isApprovalRequired === true ? "24hr_review" : "auto";

  return {
    business_id: sessionInfo?.corporateInfo.businessId,
    business_name: sessionInfo?.corporateInfo.businessName,
    customer_account_role:
      customerAccountRole || sessionInfo?.corporateInfo?.cap1Role,
    user_role: sessionInfo?.corporateInfo.role?.join(","),
    user_policy_tier: sessionInfo?.corporateInfo.policyTier,
    user_approval_process: approvalProcess,
    num_eligible_businesses: corpBusinessAccounts.length.toString() || 0,
    num_optedin_businesses:
      corpBusinessAccounts
        .filter((account) => "optedIn" in account)
        .length.toString() || 0,
    whos_booking_answer: modalMetadata?.whos_booking?.answer,
    user_policy_active: !getIsDefaultPolicySelected(policyTier),
    is_multiportal: isMultiportal,
  };
};

export const getGlobalTrackingProperties = <T extends Tenant>(
  tenant: T,
  state: StoreState<T>
) => {
  const {
    rewardsAccounts = [],
    corpBusinessAccounts = [],
    selectedAccountReferenceId,
  } = state;

  const currentRewardsAccount =
    rewardsAccounts.find(
      (account) => account.accountReferenceId === selectedAccountReferenceId
    ) ??
    (isCorpTenant(tenant)
      ? (corpBusinessAccounts as CorpBusinessAccount[]).find(
          (b) =>
            b.rewardsAccount?.accountReferenceId === selectedAccountReferenceId
        )?.rewardsAccount
      : undefined);

  const { isTravelApp } = getIsTravelApp();

  const userDeviceData = getUserDeviceData();

  const channel = (() => {
    switch (true) {
      case isTravelApp:
        return "Travel App";
      case userDeviceData.deviceType === "mobile":
        return "Mobile";
      default:
        return "Web";
    }
  })();

  return {
    customer_account_role: currentRewardsAccount?.customerAccountRole,
    product: currentRewardsAccount?.productDisplayName,
    currency: currentRewardsAccount?.rewardsBalance.currency,
    channel,
    ...userDeviceData,
    ...(isCorpTenant(tenant) &&
      getCorpUserSessionData(
        state as StoreState<"capone-corporate">,
        currentRewardsAccount?.customerAccountRole
      )),
  };
};
